/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import {
  Box,
  Button,
  FileInput,
  Group,
  NumberInput,
  Select,
  Text,
  Textarea,
  TextInput,
  Image as MantineImage,
  Radio,
  Checkbox,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { RichTextEditor } from "@mantine/rte";
import { DatePicker } from "@mantine/dates";
import { ArrowNarrowLeft } from "tabler-icons-react";

import { Link, useNavigate } from "react-router-dom";

import {
  Chart,
  chartSchema,
  useCreateChartMutation,
  useCategoriesQuery,
} from "services/api";

const Create = () => {
  const [createChart, { isLoading: isCreatingChart }] =
    useCreateChartMutation();

  const form = useForm<Chart>({
    validate: zodResolver(chartSchema),
    initialValues: {
      boardDesc: "",
      boardName: "",
      boardType: "1",
      dataSource: "",
      htmlText: "",
      htmlTextMobile: "",
      id: 0,
      isHot: false,
      mhid: 0,
      sort: 0,
      thumbnailFileName: "",
      unitName: "",
      url: "",
      display: 0,
    },
  });

  const navigate = useNavigate();

  const handleSubmit = async (values: Chart) => {
    try {
      await createChart(values).unwrap();

      navigate("../charts", { replace: true });
    } catch (e) {}
  };

  const { data: categories = [] } = useCategoriesQuery();

  const categoryOptions = categories.map((item) => ({
    label: item.name,
    value: item.id,
  }));

  const boardTypeOptions = [
    {
      label: "Tableau",
      value: "1",
    },
    {
      label: "Power BI",
      value: "2",
    },
    {
      label: "其他 HTML 類型",
      value: "3",
    },
  ];

  const boardTypeLabel =
    boardTypeOptions.find((x) => x.value === form.values.boardType)?.label ??
    "";

  return (
    <Box py={8} px={36}>
      <Text
        variant="link"
        component={Link}
        color="cyan"
        to="../charts"
        underline={false}
        sx={(theme) => ({
          display: "inline-flex",
          alignItems: "center",
          "&:hover": {
            borderBottom: "1px solid" + theme.colors.cyan[6],
          },
        })}
      >
        <ArrowNarrowLeft size={20} /> 返回上一頁
      </Text>
      <h2>新增儀表板</h2>

      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Box>
          <TextInput
            mb={12}
            label="儀表板名稱"
            required
            placeholder="名稱"
            {...form.getInputProps("boardName")}
          />

          <TextInput
            mb={12}
            label="資料來源"
            placeholder="資料來源"
            {...form.getInputProps("dataSource")}
          />

          <FileInput
            mb={8}
            label="主圖"                    
            placeholder="上傳新檔案"
            accept="image/png,image/jpeg,image/webp"
            {...form.getInputProps("thumbnail")}
          />

          <Text color="blue" size="xs">
            * 建議使用 1920x1200 之圖片上稿
          </Text>

          {form.values.thumbnail && (
            <MantineImage
              mb={12}
              mt="sm"
              width={144}
              src={URL.createObjectURL(form.values.thumbnail)}
            />
          )}

          <Select
            mb={12}
            label="儀表板類型"
            placeholder="請選擇儀表板類型"
            data={boardTypeOptions}
            {...form.getInputProps("boardType")}
          ></Select>

          <Textarea
            mb={12}
            label="桌機版儀表板"
            placeholder={`儀表板置入連結 (請貼上 ${boardTypeLabel} 嵌入語法)`}
            autosize
            {...form.getInputProps("htmlText")}
          />

          <Textarea
            mb={12}
            label="行動版儀表板"
            placeholder={`儀表板置入連結 (請貼上 ${boardTypeLabel} 嵌入語法)`}
            autosize
            {...form.getInputProps("htmlTextMobile")}
          />

          <Text size="sm">簡介</Text>
          <RichTextEditor
            controls={[
              ["bold", "italic", "underline", "strike", "clean"],
              ["h1", "h2", "h3", "h4", "h5"],
              ["unorderedList", "orderedList"],
            ]}
            css={css`
              & ol,
              ul,
              menu {
                list-style: revert;
              }
            `}
            mb={12}
            value={form.values.boardDesc}
            onChange={(text) => form.setFieldValue("boardDesc", text)}
          />

          <TextInput
            mb={12}
            label="儀表版URL"
            placeholder="ex: /charts/'自定義 URL'"
            {...form.getInputProps("url")}
          />

          <TextInput
            mb={12}
            label="建置單位"
            placeholder="建置單位"
            {...form.getInputProps("unitName")}
          />

          <Select
            mb={12}
            label="儀表板分類"
            placeholder="請選擇儀表板分類"
            data={categoryOptions}
            {...form.getInputProps("mhid")}
          ></Select>

          <Radio.Group
            my="md"
            label="資料顯示"
            required
            value={`${form.values.display}`}
            onChange={(value) => form.setFieldValue("display", +value)}
          >
            <Radio value="2" label="公開" />
            <Radio value="1" label="限市府人員" />
            <Radio value="0" label="隱藏下架" />
          </Radio.Group>

          <Group my="md">
            <Checkbox
              label="設為首頁熱門儀表板"
              checked={form.values.isHot}
              onChange={(event) =>
                form.setFieldValue("isHot", event.currentTarget.checked)
              }
            />

            <DatePicker
              placeholder="熱門起始日期"
              locale="zh-tw"
              style={{
                flex: "20% 0 0",
              }}
              maxDate={form.values.isHotTo}
              {...form.getInputProps("isHotFrom")}
              value={form.values.isHotFrom}
              withinPortal
            />

            <DatePicker
              placeholder="熱門結束日期"
              locale="zh-tw"
              style={{
                flex: "20% 0 0",
              }}
              minDate={form.values.isHotFrom}
              {...form.getInputProps("isHotTo")}
              value={form.values.isHotTo}
              withinPortal
            />
          </Group>
        </Box>

        <Box>
          <NumberInput
            label="排序"
            placeholder="1"
            {...form.getInputProps("sort")}
          />
        </Box>

        <Group mt="xl">
          <Button type="submit" color="cyan" disabled={isCreatingChart}>
            建立儀表板
          </Button>
        </Group>
      </form>
    </Box>
  );
};

export default Create;
